import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  History,
  Image as ImageIcon,
  Info,
  LandPlot,
  Loader2,
  MoreVertical,
  RefreshCw,
  Trash2,
  Utensils,
} from 'lucide-react';

import { assetService } from '../services/assetService';
import { knowledgeService } from '../services/knowledgeService';
import { isTtqSubjectEnabled, resolveTtqDockSubject, ttqService } from '../services/ttqService';

export type KnowledgeParticleOpsMoreMenuProps = {
  particleId: string;
  subject?: string | null;
  sourceAssetId?: string | null;
  /** 已在详情页：隐藏「颗粒详情」入口，改用「补充信息」若提供 */
  hideDetailLink?: boolean;
  /** 打开补充信息（对齐 Mobile knowledge-supplement） */
  onOpenSupplement?: () => void;
  /** 隐性能力：首次提取 */
  onExtractPap?: () => void;
  /** 隐性能力：重新提取 */
  onReExtractPap?: () => void;
  papExtracting?: boolean;
  showPapExtract?: boolean;
  showPapReExtract?: boolean;
  /** 删除成功后（默认回退到 /knowledge） */
  onDeleted?: () => void;
  /** 菜单展开方向：header 用 down，底部 footer 用 up */
  menuPlacement?: 'up' | 'down';
};

/** 对齐 Mobile 知识颗粒详情 OverflowMenu + 隐性能力提取 */
export function KnowledgeParticleOpsMoreMenu({
  particleId,
  subject,
  sourceAssetId,
  hideDetailLink = false,
  onOpenSupplement,
  onExtractPap,
  onReExtractPap,
  papExtracting = false,
  showPapExtract = false,
  showPapReExtract = false,
  onDeleted,
  menuPlacement = 'up',
}: KnowledgeParticleOpsMoreMenuProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [ttqAdding, setTtqAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pasBoundCount, setPasBoundCount] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const showTtq = isTtqSubjectEnabled(String(subject || ''));
  const resolvedSourceAssetId = String(sourceAssetId || '').trim();
  const busy = ttqAdding || deleting || papExtracting;

  useEffect(() => {
    if (!particleId) {
      setPasBoundCount(null);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const assets = await assetService.listPasAssetsByParticleId(particleId);
        if (!cancelled) {
          setPasBoundCount(Array.isArray(assets) ? assets.length : 0);
        }
      } catch {
        if (!cancelled) {
          setPasBoundCount(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [particleId]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  if (!particleId) return null;

  const close = () => setOpen(false);

  const handleAddToTtq = async () => {
    if (!showTtq || ttqAdding) return;
    const ttqSubject = resolveTtqDockSubject(String(subject || ''));
    setTtqAdding(true);
    setError(null);
    try {
      const session = await ttqService.createSessionWithParticle(ttqSubject, particleId);
      close();
      navigate(`/ttq/${session.id}`);
    } catch (ttqError) {
      setError(ttqError instanceof Error ? ttqError.message : '加入品题失败');
    } finally {
      setTtqAdding(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    const confirmed = window.confirm('确定删除该知识颗粒？此操作不可恢复。');
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    try {
      await knowledgeService.deleteKnowledgeParticle(particleId);
      close();
      if (onDeleted) {
        onDeleted();
      } else {
        navigate('/knowledge');
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const itemClass = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50';
  const dangerClass = 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5 disabled:opacity-50';

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-surface text-muted hover:bg-black/[0.04] hover:text-ink disabled:opacity-50"
        aria-label="知识颗粒操作"
        aria-expanded={open}
        title="知识颗粒操作"
        disabled={busy && !open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
          setError(null);
        }}
      >
        {busy && !open ? <Loader2 size={16} className="animate-spin" /> : <MoreVertical size={16} />}
      </button>
      {open ? (
        <div
          className={
            menuPlacement === 'down'
              ? 'absolute top-[calc(100%+4px)] right-0 z-30 min-w-[12rem] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg'
              : 'absolute bottom-[calc(100%+4px)] right-0 z-30 min-w-[12rem] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg'
          }
        >
          {showTtq ? (
            <button
              type="button"
              className={itemClass}
              disabled={ttqAdding}
              onClick={() => { void handleAddToTtq(); }}
            >
              {ttqAdding ? <Loader2 size={14} className="animate-spin" /> : <Utensils size={14} />}
              {ttqAdding ? '加入品题中…' : '加入品题'}
            </button>
          ) : null}

          {hideDetailLink ? (
            onOpenSupplement ? (
              <button
                type="button"
                className={itemClass}
                onClick={() => {
                  close();
                  onOpenSupplement();
                }}
              >
                <Info size={14} />
                详情
              </button>
            ) : null
          ) : (
            <Link
              to={`/knowledge/${encodeURIComponent(particleId)}`}
              className={itemClass}
              onClick={close}
            >
              <Info size={14} />
              颗粒详情
            </Link>
          )}

          <Link
            to={`/knowledge/${encodeURIComponent(particleId)}/records`}
            className={itemClass}
            onClick={close}
          >
            <History size={14} />
            学习记录
          </Link>

          <Link
            to={`/knowledge/${encodeURIComponent(particleId)}/pas`}
            className={`${itemClass} justify-between`}
            onClick={close}
          >
            <span className="inline-flex items-center gap-2">
              <LandPlot size={14} />
              实战答题纸
            </span>
            <span className="tabular-nums text-muted">
              {pasBoundCount == null ? '…' : pasBoundCount}
            </span>
          </Link>

          {resolvedSourceAssetId ? (
            <Link
              to={`/assets/${encodeURIComponent(resolvedSourceAssetId)}`}
              className={itemClass}
              onClick={close}
            >
              <ImageIcon size={14} />
              素材详情
            </Link>
          ) : null}

          {(showPapExtract || showPapReExtract) ? (
            <div className="my-1 border-t border-border" />
          ) : null}

          {showPapExtract ? (
            <button
              type="button"
              className={itemClass}
              disabled={papExtracting}
              onClick={() => {
                close();
                onExtractPap?.();
              }}
            >
              {papExtracting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              提取隐性能力
            </button>
          ) : null}

          {showPapReExtract ? (
            <button
              type="button"
              className={itemClass}
              disabled={papExtracting}
              onClick={() => {
                close();
                onReExtractPap?.();
              }}
            >
              {papExtracting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              重新提取隐性能力
            </button>
          ) : null}

          <div className="my-1 border-t border-border" />

          <button
            type="button"
            className={dangerClass}
            disabled={deleting}
            onClick={() => { void handleDelete(); }}
          >
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            删除知识颗粒
          </button>

          {error ? (
            <p className="border-t border-border px-3 py-2 text-[11px] text-danger">{error}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
